import React from "react";
import { Lock, Download, AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import { api } from "@/lib/api";

export function GatedPill({ note }) {
  return (
    <span data-testid="gated-pill" className="inline-flex items-center gap-1 bg-blaze/15 border border-blaze/50 text-blaze text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5">
      <Lock className="w-3 h-3" strokeWidth={2} /> {note || "Activates with payments"}
    </span>
  );
}

// Distinct from a payment-gated pill: this metric has no valid sample yet (e.g. no sale has
// cleared), so it reads NOT MEASURED — never a fake 0 / 0%.
export function NotMeasuredPill({ note }) {
  return (
    <span data-testid="not-measured-pill" className="inline-flex items-center gap-1 bg-[#d3a24e]/15 border border-[#d3a24e]/50 text-[#d3a24e] text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5">
      Not measured
    </span>
  );
}

export function centsToUsd(c) {
  if (c == null) return "—";
  return `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// Amber wrong-zero warning pill: a sold-side tile computed a suspicious ZERO while the
// settled-orders ledger shows a real sale. Names WHICH check tripped, never a bare amber.
export function WarningPill() {
  return (
    <span data-testid="warning-pill" className="inline-flex items-center gap-1 bg-blaze/15 border border-blaze/50 text-blaze text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5">
      <AlertTriangle className="w-3 h-3" strokeWidth={2} /> Check failed
    </span>
  );
}

// Money tile: renders "—" + a pill when payment-dependent (gated), not-yet-measurable
// (not_measured), or a wrong-zero mismatch (warning — amber, names the tripped check).
export function Tile({ icon: Icon, label, tile, isPct }) {
  const gated = tile?.display_state === "stripe_gated";
  const notMeasured = tile?.display_state === "not_measured";
  const warning = tile?.display_state === "warning";
  const blank = gated || notMeasured || warning;
  return (
    <div
      className={`border bg-surface p-5 flex flex-col ${warning ? "border-blaze/60" : "border-sniper"}`}
      data-testid={`tile-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </span>
        {gated && <GatedPill note={tile.note} />}
        {notMeasured && <NotMeasuredPill note={tile.note} />}
        {warning && <WarningPill />}
      </div>
      {blank ? (
        <>
          <div className={`font-heading text-3xl mt-2 ${warning ? "text-blaze" : "text-muted-foreground"}`}>—</div>
          {warning && tile?.warning && (
            <p className="text-[10px] font-mono text-blaze mt-1 leading-snug" data-testid="tile-warning">{tile.warning}</p>
          )}
          {tile?.note && <p className="text-[10px] font-mono text-muted-foreground mt-1 leading-snug">{tile.note}</p>}
        </>
      ) : (
        <>
          <div className="font-heading text-3xl mt-2 text-foreground" data-testid="tile-value">
            {isPct ? `${tile.value}%` : centsToUsd(tile.value)}
          </div>
          {/* Source note on LIVE money tiles too — same wording on every money tile so they never drift. */}
          {tile?.note && <p className="text-[10px] font-mono text-muted-foreground mt-1 leading-snug" data-testid="tile-source-note">{tile.note}</p>}
        </>
      )}
    </div>
  );
}

// Count tile: raw row counts, always live.
export function CountTile({ icon: Icon, label, value }) {
  return (
    <div className="border border-sniper bg-surface p-5" data-testid={`count-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <div className="font-heading text-3xl mt-2 text-foreground" data-testid="count-value">{value ?? 0}</div>
    </div>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div className="mt-10 mb-3">
      <h2 className="font-heading text-2xl uppercase tracking-tight text-foreground">{children}</h2>
      {sub && <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export function secondsToDuration(s) {
  s = Math.max(0, Math.round(s || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

export async function downloadCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error("CSV export failed — please try again.");
  }
}

export function ExportButton({ path, filename, testId }) {
  return (
    <button onClick={() => downloadCsv(path, filename)} data-testid={testId}
      className="inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-1.5 transition-colors">
      <Download className="w-3.5 h-3.5" /> CSV
    </button>
  );
}

export function EmptyNote({ children }) {
  return <p className="p-4 text-sm text-muted-foreground font-body">{children}</p>;
}

// Horizontal proportional bars — used for sources, device/browser, geo, funnel.
// A row may carry an optional amber `warning` string (wrong-zero / mismatch guard).
export function BarList({ rows, testId, valueFmt }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid={testId}>
      {rows.length === 0 && <EmptyNote>No data yet.</EmptyNote>}
      {rows.map((r) => (
        <div key={r.label} className="px-3 py-2.5">
          <div className="flex items-center justify-between font-mono text-xs mb-1.5">
            <span className="text-secondary-foreground truncate pr-3 flex items-center gap-1">
              {r.warning && <AlertTriangle className="w-3 h-3 text-blaze shrink-0" strokeWidth={2} />}
              {r.label}
            </span>
            <span className="text-foreground whitespace-nowrap">{valueFmt ? valueFmt(r.value, r) : r.value}{r.pct != null ? <span className="text-muted-foreground"> · {r.pct}%</span> : null}</span>
          </div>
          <div className="h-1.5 bg-obsidian">
            <div className={`h-full ${r.warning ? "bg-blaze" : "bg-olive"}`} style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          {r.warning && (
            <p className="text-[10px] font-mono text-blaze mt-1 leading-snug" data-testid={`barlist-warning-${String(r.label).replace(/\s+/g, "-").toLowerCase()}`}>{r.warning}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export function CollectingNote({ since }) {
  return (
    <span className="text-[10px] font-mono uppercase text-muted-foreground">
      First-party tracking · collecting since {since || "now"}
    </span>
  );
}
